/**
 * footer-config.js — Single source of truth for all footer content.
 *
 * Governance rules:
 *   - Regulatory text changes require Legal & Compliance sign-off.
 *   - Product links must match the live routing table in App.js.
 *   - Adding a region requires Compliance and Brand approval.
 *   - Version and lastReviewed must be updated on every change.
 *
 * Version: 1.0.1
 * Last reviewed: 2026-05-20
 */

/* ── Section 1: Corporate Identity ──────────────────────────────────────── */
export const CORPORATE = {
  legalName: 'PWW Capital',
  tradeName: 'PWWCapital™',
  description:
    'PWW Capital provides an online account portal and support experience for approved users.',
  address: {
    line1: 'Customer Support',
    line2: 'support@pwwcapital.com',
    line3: 'fraud@pwwcapital.com',
  },
  registrationNumbers: [
    'Services subject to identity verification',
    'Availability may vary by region',
    'See legal disclosures for current terms',
  ],
  globalPresence: 'Access is limited to approved users. Feature availability depends on account status and current terms.',
};

/* ── Section 2: Regulatory & Compliance (text only) ─────────────────────── */
export const REGULATORY_ITEMS = [
  'This portal is intended for existing approved users only.',
  'Public account creation is currently disabled.',
  'Feature availability depends on account status and internal review.',
  'Users should rely on published terms and support channels for current information.',
  'Do not share passwords, one-time codes, or account access details with anyone.',
  'Report suspicious account activity using the published support contacts.',
];

export const COMPLIANCE_BADGES = [
  'HTTPS Access',
  'MFA Available',
  'Account Alerts',
  'Privacy Controls',
  'Support Contact',
  'Published Terms',
];

/* ── Section 3: Security & Fraud Prevention ─────────────────────────────── */
export const SECURITY_LINKS = [
  { label: 'How We Protect You',           to: '/security' },
  { label: 'Fraud Prevention Center',      to: '/security/fraud-prevention' },
  { label: 'Report Suspicious Activity',   to: '/security/report-fraud' },
  { label: 'Security Alerts',              to: '/security/alerts' },
  { label: 'Two-Factor Authentication',    to: '/security/2fa' },
  { label: 'Online Account Safety Tips',   to: '/security/safety-tips' },
  { label: 'Identity Theft Protection',    to: '/security/identity-theft' },
  { label: 'Phishing Awareness',           to: '/security/phishing' },
];

/* ── Section 4: Platform Links ───────────────────────────────────────────── */
export const BANKING_PRODUCTS = [
  { label: 'Client Login',         to: '/login' },
  { label: 'Support Center',       to: '/support-center' },
  { label: 'Security Center',      to: '/security-center' },
  { label: 'Contact Support',      to: '/contact' },
  { label: 'Privacy Policy',       to: '/privacy' },
  { label: 'Terms & Conditions',   to: '/terms' },
];

/* ── Section 5: Legal & Policy Documents ────────────────────────────────── */
export const LEGAL_LINKS = [
  { label: 'Terms & Conditions',                  to: '/terms',               external: false },
  { label: 'Privacy Policy',                      to: '/privacy',             external: false },
  { label: 'Cookie Policy',                       to: '/cookies',             external: false },
  { label: 'Electronic Communications Disclosure', to: '/legal/ecd',          external: false },
  { label: 'Accessibility Statement',             to: '/accessibility',       external: false },
  { label: 'Intellectual Property Notice',        to: '/legal/ip-notice',     external: false },
  { label: 'Tax Documentation (FATCA/CRS)',       to: '/legal/tax-docs',      external: false },
  { label: 'Annual Report',                       to: '/legal/annual-report', external: false },
  { label: 'Whistleblower Policy',                to: '/legal/whistleblower', external: false },
  { label: 'Modern Slavery Statement',            to: '/legal/modern-slavery', external: false },
];

/* ── Section 6: Region & Language Selectors ─────────────────────────────── */
export const REGIONS = [
  { code: 'US', name: 'United States' },
  { code: 'GB', name: 'United Kingdom' },
  { code: 'EU', name: 'European Union' },
  { code: 'NG', name: 'Nigeria' },
  { code: 'GH', name: 'Ghana' },
  { code: 'ZA', name: 'South Africa' },
  { code: 'KE', name: 'Kenya' },
  { code: 'SG', name: 'Singapore' },
  { code: 'AE', name: 'United Arab Emirates' },
  { code: 'CA', name: 'Canada' },
  { code: 'AU', name: 'Australia' },
  { code: 'IN', name: 'India' },
];

export const LANGUAGES = [
  { code: 'en',    name: 'English' },
  { code: 'fr',    name: 'Français' },
  { code: 'es',    name: 'Español' },
  { code: 'ar',    name: 'العربية' },
  { code: 'zh',    name: '中文' },
  { code: 'pt',    name: 'Português' },
  { code: 'de',    name: 'Deutsch' },
  { code: 'sw',    name: 'Kiswahili' },
  { code: 'ha',    name: 'Hausa' },
  { code: 'yo',    name: 'Yorùbá' },
];
